// Phase 16.10 Plan 01 — D-08 shared chargeable-client query.
// A client is "chargeable" iff: status='active' AND fanbasis_customer_id IS NOT NULL
// AND has at least one payment_methods row with status='active' AND is_primary=true.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ChargeableClient } from "@/lib/fanbasis";

export function useChargeableClients(): UseQueryResult<
  ChargeableClient[],
  Error
> {
  return useQuery<ChargeableClient[], Error>({
    queryKey: ["chargeable-clients"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data: clients, error: clientsError } = await supabase
        .from("clients")
        .select("id, company_name, package, fanbasis_customer_id")
        .eq("status", "active")
        .not("fanbasis_customer_id", "is", null)
        .order("company_name", { ascending: true });

      if (clientsError) throw clientsError;
      if (!clients || clients.length === 0) return [];

      const ids = clients.map((c: { id: string }) => c.id);

      const { data: pms, error: pmError } = await supabase
        .from("payment_methods")
        .select("id, client_id, fanbasis_payment_method_id, last4, brand, is_primary, status")
        .eq("status", "active")
        .eq("is_primary", true)
        .in("client_id", ids);

      if (pmError) throw pmError;

      const pmByClient = new Map<string, {
        id: string;
        fanbasis_payment_method_id: string;
        last4: string;
        brand: string | null;
      }>();
      (pms ?? []).forEach((pm: {
        id: string;
        client_id: string;
        fanbasis_payment_method_id: string;
        last4: string;
        brand: string | null;
      }) => {
        if (!pmByClient.has(pm.client_id)) {
          pmByClient.set(pm.client_id, {
            id: pm.id,
            fanbasis_payment_method_id: pm.fanbasis_payment_method_id,
            last4: pm.last4,
            brand: pm.brand,
          });
        }
      });

      const out: ChargeableClient[] = [];
      for (const c of clients as Array<{
        id: string;
        company_name: string;
        package: "elite" | "standard" | null;
        fanbasis_customer_id: string | null;
      }>) {
        const pm = pmByClient.get(c.id);
        if (!pm) continue;
        if (!c.fanbasis_customer_id) continue;
        out.push({
          id: c.id,
          company_name: c.company_name,
          package: c.package,
          fanbasis_customer_id: c.fanbasis_customer_id,
          primary_payment_method: pm,
        });
      }
      return out;
    },
  });
}
